/**
 * Hook pour le socket de notification global.
 * 
 * Connecte un socket Socket.IO dédié aux notifications ciblées (invitations, etc.)
 * et s'identifie via une user-room pour recevoir les événements personnels.
 * 
 * Gère la logique "déjà en jeu" :
 * - Si l'utilisateur est en partie contre des humains (et doNotDisturbInGame),
 *   les invitations sont auto-déclinées avec une simple notification informative.
 * - Si la partie est uniquement contre des robots (et allowInviteIfBotsOnly),
 *   le flux normal d'invitation (InvitationToast interactif) s'applique.
 */

import type { LibraryNewShareEvent } from '../types/library-notifications';
import { useEffect, useRef, useCallback, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import type { AvailabilityProfile, DraftGameMode } from '../types/draft';
import type { GameConnectionInfo, UserPreferences } from '../types/app';
import { BACKEND_URL } from '../config';
const INVITATION_TIMEOUT_MS = 10_000;
/** Délai avant réaffichage automatique d'un toast d'alerte défi mis en "Plus tard" */
const DEFI_ALERT_REAPPEAR_MS = 90_000;
/** Délai avant connexion automatique à une table de défi (repoussé à chaque "Plus tard") — utilisé si aucun rendez-vous n'est connu */
const DEFI_ALERT_FORCE_JOIN_MS = 180_000;
/** Quand un rendez-vous précis est connu (CIA / championnat par équipe) : connexion auto 1 minute avant l'heure convenue */
const RDV_FORCE_JOIN_LEAD_MS = 60_000;
/** Délai plancher pour laisser le temps de réagir même si le rendez-vous est déjà dépassé */
const RDV_FORCE_JOIN_MIN_DELAY_MS = 20_000;

export interface InvitationData {
  draftId: string;
  targetPseudo: string;
  fromPseudo: string;
  fromPseudoDisplay: string;
  config: {
    gameMode: DraftGameMode;
    nombreJoueurs: 3 | 4 | 5;
  };
  compet: boolean;
  players: Array<{ pseudo: string; pseudoDisplay: string }>;
  availability: AvailabilityProfile;
  receivedAt: number;
}

/**
 * Table de défi (CIA / défi libre / défi par équipe) créée automatiquement
 * par le serveur Java legacy, sur laquelle l'utilisateur courant est attendu
 * mais pas encore connecté. Voir backend/src/services/defi-table-alerts.ts.
 */
export interface DefiTableAlert {
  tableKey: string;
  ip: string;
  port: number;
  idserv: 'defis';
  nbj: number;
  nomtable?: string;
  comm?: string;
  defiKind?: string;
  defiKindLabel?: string;
  /** Rendez-vous confirmé (CIA / championnat par équipe), ISO 8601, si résolu côté backend */
  rdv?: string;
  receivedAt: number;
  /** Horodatage auquel cette table connectera automatiquement l'utilisateur si non rejointe entre-temps */
  forceJoinAt: number;
  /**
   * 'fixed' : forceJoinAt dérive d'un rendez-vous connu (rdv - 1 min) et n'est
   * jamais repoussé par "Plus tard" (seule la visibilité du toast l'est).
   * 'relative' : aucun rendez-vous connu, forceJoinAt est répoussé de
   * DEFI_ALERT_FORCE_JOIN_MS à chaque "Plus tard".
   */
  forceJoinMode: 'fixed' | 'relative';
  /** Si > now, le toast est snoozé ("Plus tard") et masqué jusqu'à cet horodatage */
  hiddenUntil: number;
}

function detectPlatform(): string {
  if (typeof window === 'undefined') return 'web';
  const capacitor = (window as unknown as { Capacitor?: { getPlatform?: () => string; isNativePlatform?: () => boolean } }).Capacitor;
  if (capacitor?.isNativePlatform?.()) {
    return capacitor.getPlatform?.() ?? 'native';
  }
  return 'web';
}

interface UseNotificationSocketOptions {
  pseudo: string;
  /** Espace courant (détente / compétition), utilisé pour les statistiques d'usage admin. */
  compet?: boolean;
  /** Licence compétition valide, utilisée pour les statistiques d'usage admin. */
  hasCompetitionAccess?: boolean;
  onInvitationAccepted?: (draftId: string) => void;
  /** Info de partie en cours (null si pas de partie) */
  gameInfo?: GameConnectionInfo | null;
  /** Préférences utilisateur */
  preferences?: UserPreferences;
  /** Callback pour afficher un toast global (AppContext.showToast) */
  onShowToast?: (toast: {
    message: string;
    type: 'info' | 'game' | 'chat' | 'invitation' | 'table' | 'system';
    targetTab?: 'chat' | 'tables' | 'game' | 'settings';
    subtitle?: string;
    duration?: number;
  }) => void;
  /** Nouveau partage bibliothèque reçu (socket user room) */
  onLibraryNewShare?: (data: LibraryNewShareEvent) => void;
  /** Une table de défi legacy attend l'utilisateur (dès réception, avant tout affichage/dismiss) */
  onDefiTableReady?: (alert: DefiTableAlert) => void;
  /**
   * Le délai de connexion automatique est écoulé pour cette table de défi et
   * l'utilisateur n'est pas déjà en train de jouer ailleurs : on le connecte.
   */
  onForceJoinDefiTable?: (alert: DefiTableAlert) => void;
}

type DecisionMap = Map<string, boolean>;

export function useNotificationSocket({
  pseudo,
  compet,
  hasCompetitionAccess,
  onInvitationAccepted,
  gameInfo,
  preferences,
  onShowToast,
  onLibraryNewShare,
  onDefiTableReady,
  onForceJoinDefiTable,
}: UseNotificationSocketOptions) {
  const socketRef = useRef<Socket | null>(null);
  const [pendingInvitations, setPendingInvitations] = useState<InvitationData[]>([]);
  // Toutes les alertes défi armées (visibles ou snoozées) — la visibilité et
  // la connexion automatique sont dérivées de `nowTick` à chaque seconde.
  const [defiAlerts, setDefiAlerts] = useState<DefiTableAlert[]>([]);
  const [nowTick, setNowTick] = useState(() => Date.now());
  const onInvitationAcceptedRef = useRef(onInvitationAccepted);
  onInvitationAcceptedRef.current = onInvitationAccepted;
  const onShowToastRef = useRef(onShowToast);
  onShowToastRef.current = onShowToast;
  const onLibraryNewShareRef = useRef(onLibraryNewShare);
  onLibraryNewShareRef.current = onLibraryNewShare;
  const onDefiTableReadyRef = useRef(onDefiTableReady);
  onDefiTableReadyRef.current = onDefiTableReady;
  const onForceJoinDefiTableRef = useRef(onForceJoinDefiTable);
  onForceJoinDefiTableRef.current = onForceJoinDefiTable;
  const gameInfoRef = useRef(gameInfo);
  gameInfoRef.current = gameInfo;
  const preferencesRef = useRef(preferences);
  preferencesRef.current = preferences;
  const decisionsRef = useRef<DecisionMap>(new Map());
  // Capturés au moment du connect, réutilisés lors des changements d'espace en cours de session.
  const competRef = useRef(compet);
  competRef.current = compet;
  const hasCompetitionAccessRef = useRef(hasCompetitionAccess);
  hasCompetitionAccessRef.current = hasCompetitionAccess;

  useEffect(() => {
    if (!pseudo) return;

    const socket = io(BACKEND_URL, {
      transports: ['websocket', 'polling'],
    });

    socket.on('connect', () => {
      socket.emit('identify', { pseudo });
      // Statistiques d'usage : une "session" = du connect au disconnect de ce socket,
      // qui vit tout le temps où l'utilisateur reste identifié dans l'app.
      socket.emit('usage:session_start', {
        pseudo,
        compet: competRef.current,
        hasCompetitionAccess: hasCompetitionAccessRef.current,
        platform: detectPlatform(),
      });
    });

    socket.on('library:new_share', (data: LibraryNewShareEvent) => {
      onLibraryNewShareRef.current?.(data);
    });

    socket.on('defi_table_ready', (data: Omit<DefiTableAlert, 'receivedAt' | 'forceJoinAt' | 'forceJoinMode' | 'hiddenUntil'>) => {
      const now = Date.now();
      const rdvTimestamp = data.rdv ? new Date(data.rdv).getTime() : NaN;
      const hasValidRdv = Number.isFinite(rdvTimestamp);
      const forceJoinMode: DefiTableAlert['forceJoinMode'] = hasValidRdv ? 'fixed' : 'relative';
      const forceJoinAt = hasValidRdv
        ? Math.max(now + RDV_FORCE_JOIN_MIN_DELAY_MS, rdvTimestamp - RDV_FORCE_JOIN_LEAD_MS)
        : now + DEFI_ALERT_FORCE_JOIN_MS;
      const alert: DefiTableAlert = {
        ...data,
        receivedAt: now,
        forceJoinAt,
        forceJoinMode,
        hiddenUntil: 0,
      };
      setDefiAlerts(prev => {
        if (prev.some(a => a.tableKey === data.tableKey)) return prev;
        return [...prev, alert];
      });
      onDefiTableReadyRef.current?.(alert);
    });

    socket.on('draft:invitation', (data: Omit<InvitationData, 'receivedAt'>) => {
      const prefs = preferencesRef.current;
      const currentGameInfo = gameInfoRef.current;
      const isInGame = currentGameInfo !== null;

      if (isInGame && prefs?.doNotDisturbInGame) {
        const hasOtherHumans = currentGameInfo?.hasOtherHumans;
        const isBotsOnly = hasOtherHumans === false;
        const shouldAllowDespiteDnd = isBotsOnly && prefs.allowInviteIfBotsOnly;

        if (!shouldAllowDespiteDnd) {
          socket.emit('draft:invitation_response', {
            draftId: data.draftId,
            pseudo,
            accepted: false,
          });
          onShowToastRef.current?.({
            message: `${data.fromPseudoDisplay} vous a invité à jouer`,
            subtitle: 'Décliné automatiquement — vous êtes en partie',
            type: 'invitation',
            targetTab: 'tables',
            duration: 6000,
          });
          return;
        }
      }

      const invitation: InvitationData = {
        ...data,
        receivedAt: Date.now(),
      };
      setPendingInvitations(prev => {
        if (prev.some(inv => inv.draftId === data.draftId)) return prev;
        return [...prev, invitation];
      });

      const autoAccept = data.availability !== 'timide';
      decisionsRef.current.set(data.draftId, autoAccept);

      setTimeout(() => {
        const accepted = decisionsRef.current.get(data.draftId) ?? autoAccept;
        decisionsRef.current.delete(data.draftId);
        socket.emit('draft:invitation_response', { draftId: data.draftId, pseudo, accepted });
        if (accepted) {
          window.dispatchEvent(new CustomEvent('join-draft-from-invitation', {
            detail: { draftId: data.draftId },
          }));
          onInvitationAcceptedRef.current?.(data.draftId);
        }
        setPendingInvitations(prev => prev.filter(inv => inv.draftId !== data.draftId));
      }, INVITATION_TIMEOUT_MS);
    });

    socketRef.current = socket;

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [pseudo]);

  // Tient à jour l'espace (détente/compétition) de la session d'usage en cours
  // lorsque le joueur bascule, sans recréer la connexion socket.
  useEffect(() => {
    if (compet === undefined) return;
    socketRef.current?.emit('usage:espace_update', { compet });
  }, [compet]);

  const respondToInvitation = useCallback((draftId: string, accepted: boolean) => {
    decisionsRef.current.set(draftId, accepted);
  }, []);

  const removeInvitation = useCallback((draftId: string) => {
    setPendingInvitations(prev => prev.filter(inv => inv.draftId !== draftId));
  }, []);

  /** Retire définitivement une alerte (rejointe manuellement, table disparue, utilisateur déjà connecté...) */
  const removeDefiTableAlert = useCallback((tableKey: string) => {
    setDefiAlerts(prev => prev.filter(a => a.tableKey !== tableKey));
  }, []);

  /**
   * "Plus tard" : masque le toast pendant DEFI_ALERT_REAPPEAR_MS (il réapparaît
   * ensuite tout seul). Si aucun rendez-vous précis n'est connu, repousse
   * d'autant la connexion automatique ; si un rendez-vous est connu (mode
   * 'fixed'), la connexion automatique reste calée sur l'heure convenue.
   */
  const snoozeDefiTableAlert = useCallback((tableKey: string) => {
    const now = Date.now();
    setDefiAlerts(prev => prev.map(a => (
      a.tableKey === tableKey
        ? {
            ...a,
            hiddenUntil: now + DEFI_ALERT_REAPPEAR_MS,
            forceJoinAt: a.forceJoinMode === 'fixed' ? a.forceJoinAt : now + DEFI_ALERT_FORCE_JOIN_MS,
          }
        : a
    )));
  }, []);

  // Horloge à la seconde, active uniquement tant qu'il y a des alertes défi en cours.
  useEffect(() => {
    if (defiAlerts.length === 0) return;
    const interval = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [defiAlerts.length]);

  // Connexion automatique : dès que le délai est écoulé, si l'utilisateur n'est
  // pas déjà en train de jouer ailleurs (jamais on ne l'arrache d'une partie en cours).
  useEffect(() => {
    if (defiAlerts.length === 0) return;
    if (gameInfoRef.current) return; // Une autre partie est en cours : on retentera au prochain tick.

    const due = defiAlerts.filter(a => nowTick >= a.forceJoinAt);
    if (due.length === 0) return;

    setDefiAlerts(prev => prev.filter(a => !due.some(d => d.tableKey === a.tableKey)));
    for (const alert of due) {
      onForceJoinDefiTableRef.current?.(alert);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nowTick, defiAlerts]);

  const pendingDefiTableAlerts = defiAlerts.filter(a => a.hiddenUntil <= nowTick);

  return {
    pendingInvitations,
    respondToInvitation,
    removeInvitation,
    /** Alertes actuellement affichées en toast (masque les alertes snoozées) */
    pendingDefiTableAlerts,
    /** Toutes les alertes armées, y compris celles snoozées (masquées) — pour la synchro avec la surbrillance */
    allDefiTableAlerts: defiAlerts,
    removeDefiTableAlert,
    snoozeDefiTableAlert,
    socket: socketRef.current,
  };
}
